import { useFlags } from "launchdarkly-react-client-sdk";
import {
  genderOptions,
  patientStatusOptions,
  trackerSurgeryStatusOptions,
  communicationOptions,
  programOptions,
  postoperativeOutcomeOptions,
} from "../../forms/constants/details";
import { formatISO, isValid, format } from "date-fns/esm";
import { getLocaleDateString } from "../../../utils/dates";
import { Paper, Box, FormControlLabel, Checkbox, TextField } from "@mui/material";
import { SystemStyleObject } from "@mui/system/styleFunctionSx/styleFunctionSx";
import { theme } from "../../../styles/theme";
import FormControl from "@mui/material/FormControl";
import React, { useState, useEffect } from "react";

import Avatar from "../../styled/avatar/Avatar";
import DatePicker from "../../styled/date-picker/DatePicker";
import SearchableSelect from "../../styled/searchable-select/SearchableSelect";
import AsyncSearchSelectV2 from "../../styled/async-search-select-v2/AsyncSearchSelectV2";
import Section from "../Section";
import TagsInput from "../../styled/tags-input/TagsInput";
import TextFieldInput from "../../styled/text-field-input/TextFieldInput";
import UserRaceDropdown from "../../users/UserRaceDropdown";
import TagsInputDeprecated from "components/styled/tags-input-deprecated/TagsInputDeprecated";
import PatientAppointmentSummary from "components/patients/PatientAppointmentSummary";
import Modal from "components/styled/modal/Modal";
import { Option } from "../../styled/async-search-select-v2/AsyncSearchSelectV2.types";
import { fetchServiceLines } from "../../../hooks/useServiceLines";
import { fetchSurgeryTypes } from "../../../hooks/useSurgeryTypes";
import { createHospital, fetchHospitals } from "../../../hooks/useHospitals";
import { createSurgeon, fetchSurgeons } from "../../../hooks/useSurgeons";
import {
  createComorbidity,
  fetchComorbidities,
} from "../../../hooks/useComorbidities";
import { createClearance, fetchClearances } from "../../../hooks/useClearances";
import {
  mapSelectionToStrings,
  mapStringsToOptions,
} from "../../../utils/healthSelections";
import { useCurrentUser } from "features/authentication/useCurrentUser";
import { canManageCommunicationPreferences } from "../../../utils/communicationPreferences";

const visitTypeOptions = [
  { label: "IP", value: "IP" },
  { label: "OP", value: "OP" },
  { label: "PROC", value: "PROC" },
];

const getSingleOptionValue = (value: Option | Option[] | null): string => {
  if (Array.isArray(value)) {
    return value[0]?.value || "";
  }

  return value?.value || "";
};

type CommunicationDetails = {
  communications?: string | null;
  opt_in_sms?: boolean;
  opt_in_email?: boolean;
};

const getCommunicationsValue = (
  details: CommunicationDetails,
): string | null => {
  if (details.communications) return details.communications;

  if (
    typeof details.opt_in_sms !== "boolean" ||
    typeof details.opt_in_email !== "boolean"
  ) {
    return null;
  }

  if (details.opt_in_sms && details.opt_in_email) return "All";
  if (details.opt_in_sms) return "SMS";
  if (details.opt_in_email) return "Email";
  return "None";
};

const DetailsFormSection = ({
  details,
  setDetails,
  userType,
  setTouchedFields,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setDetails: any;
  userType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setTouchedFields: any;
}): JSX.Element => {
  const { tagsV1 } = useFlags();
  const { data: currentUser } = useCurrentUser();
  const canManagePreferences = canManageCommunicationPreferences(currentUser);

  const [date, setDate] = useState<Date | null>(
    details.birth_date
      ? new Date(details.birth_date?.replace(/-/g, "/"))
      : null,
  );
  const [referralDate, setReferralDate] = useState<Date | null>(
    details.referral_date
      ? new Date(details.referral_date?.replace(/-/g, "/"))
      : null,
  );
  const [enrolledDate, setEnrolledDate] = useState<Date | null>(
    details.enrolled_date
      ? new Date(details.enrolled_date?.replace(/-/g, "/"))
      : null,
  );
  const [surgeryDay, setSurgeryDay] = useState<Date | null>(
    details.surgery_date
      ? new Date(details.surgery_date?.replace(/-/g, "/"))
      : null,
  );
  const [pendingSurgeryDay, setPendingSurgeryDay] = useState<Date | null>(null);
  const [surgeryDateError, setSurgeryDateError] = useState("");
  const [postoperativeEdVisitDate, setPostoperativeEdVisitDate] =
    useState<Date | null>(
      details.postoperative_ed_visit_date
        ? new Date(details.postoperative_ed_visit_date.replace(/-/g, "/"))
        : null,
    );
  const [postoperativeReadmissionDate, setPostoperativeReadmissionDate] =
    useState<Date | null>(
      details.postoperative_readmission_date
        ? new Date(details.postoperative_readmission_date.replace(/-/g, "/"))
        : null,
    );
  const race = details?.races?.[0];
  const mainContactAtPractice =
    details?.health_system_contact ||
    details?.referred_patient?.health_system_contact ||
    "";

  useEffect(() => {
    (async () => {
      if (date) {
        if (isValid(date) && date.getFullYear() > 1900) {
          const isoDate = formatISO(date);
          setDetails((details) => ({ ...details, birth_date: isoDate }));
        }
      }
      if (referralDate) {
        if (isValid(referralDate) && referralDate.getFullYear() > 1900) {
          const isoReferralDate = formatISO(referralDate);
          setDetails((details) => ({
            ...details,
            referral_date: isoReferralDate,
          }));
        }
      }
      if (enrolledDate) {
        if (isValid(enrolledDate) && enrolledDate.getFullYear() > 1900) {
          const isoEnrolledDate = formatISO(enrolledDate);
          setDetails((details) => ({
            ...details,
            enrolled_date: isoEnrolledDate,
          }));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, referralDate, enrolledDate]);

  const applySurgeryDate = (value: Date, includeScheduledStatus = false) => {
    const formattedValue = format(value, "yyyy-MM-dd");
    const backendWillSchedule =
      includeScheduledStatus &&
      details.surgery_status === "TBD" &&
      !details.surgery_date;

    setSurgeryDay(value);
    setDetails((details) => ({
      ...details,
      surgery_date: formattedValue,
      ...(includeScheduledStatus ? { surgery_status: "Scheduled" } : {}),
    }));
    setTouchedFields((touchedFields) => ({
      ...touchedFields,
      surgery_date: formattedValue,
      ...(includeScheduledStatus && !backendWillSchedule
        ? { surgery_status: "Scheduled" }
        : {}),
    }));
  };

  const closeSurgeryDateConfirmation = () => {
    setPendingSurgeryDay(null);
  };

  const confirmSurgeryDateChange = () => {
    if (!pendingSurgeryDay) return;

    applySurgeryDate(pendingSurgeryDay, true);
    setPendingSurgeryDay(null);
  };
  const detailFormInputSx = {
    display: "flex",
    flexDirection: {
      xs: "column",
      sm: "row",
    },
    width: "100%",
    margin: (theme) => theme.spacing(1.5),
    gap: (theme) => theme.spacing(3),
  };

  return (
    <Section title="Details">
      <Paper
        sx={{
          padding: (theme) => theme.spacing(4),
          borderRadius: (theme) => theme.spacing(2),
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: {
              xs: "column",
              md: "row",
            },
            justifyContent: "space-between",
            gap: (theme) => theme.spacing(3),
            marginBottom: (theme) => theme.spacing(2),
            fontSize: "14px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              minWidth: 0,
            }}
          >
            <Avatar
              user={details}
              diameter={theme.spacing(10)}
              style={{
                marginX: (theme) => theme.spacing(1.5),
                fontWeight: "bold",
                fontSize: "23px",
                marginBottom: theme.spacing(2),
              }}
            />
            <Box
              sx={{
                marginLeft: (theme) => theme.spacing(2),
                display: "flex",
                flexDirection: "column",
                paddingTop: (theme) => theme.spacing(1),
                minWidth: 0,
              }}
            >
              <span style={{ fontWeight: "bold" }}>
                {userType.toUpperCase()}
              </span>
              <span>
                {userType === "patient" ? "MEMBER SINCE " : "CREATED ON "}
                {getLocaleDateString(details?.created_at).toUpperCase()}
              </span>
              <span
                style={{
                  color: theme.palette.text.secondary,
                  overflowWrap: "break-word",
                }}
              >
                ID: {details.id}
              </span>
            </Box>
          </Box>
          {userType === "patient" && (
            <Box sx={{ width: { xs: "100%", md: "48%" } }}>
              <PatientAppointmentSummary
                patientId={details.id}
                variant="inline"
              />
            </Box>
          )}
        </Box>
        <Box sx={detailFormInputSx as SystemStyleObject}>
          <FormControl sx={{ width: "100%" }}>
            <TextFieldInput
              label="First name"
              value={details.first_name}
              onChange={(value) => {
                setDetails((details) => ({ ...details, first_name: value }));
                setTouchedFields((touchedFields) => ({
                  ...touchedFields,
                  first_name: value,
                }));
              }}
            />
          </FormControl>
          <FormControl sx={{ width: "100%" }}>
            <TextFieldInput
              label="Last name"
              value={details.last_name}
              onChange={(value) => {
                setDetails((details) => ({ ...details, last_name: value }));
                setTouchedFields((touchedFields) => ({
                  ...touchedFields,
                  last_name: value,
                }));
              }}
            />
          </FormControl>
        </Box>
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Preferred Name"
                value={details.preferred_name}
                onChange={(value) => {
                  setDetails((details) => ({
                    ...details,
                    preferred_name: value,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    preferred_name: value,
                  }));
                }}
              />
            </FormControl>
            <Box sx={{ width: "100%" }} />
          </Box>
        )}
        <Box sx={detailFormInputSx as SystemStyleObject}>
          <FormControl sx={{ width: "100%" }}>
            <SearchableSelect
              label="Gender Identity"
              setSelectedItem={(value) => {
                setDetails((details) => ({
                  ...details,
                  gender_identity: value,
                }));
                setTouchedFields((touchedFields) => {
                  return {
                    ...touchedFields,
                    gender_identity: value?.value || null,
                  };
                });
              }}
              value={details.gender_identity || null}
              options={genderOptions}
            />
          </FormControl>
          <Box sx={{ width: "100%" }}>
            <UserRaceDropdown
              onChange={(value) => {
                setDetails((details) => ({
                  ...details,
                  races: value?.value ? [value] : [],
                }));
                setTouchedFields((touchedFields) => ({
                  ...touchedFields,
                  races: value?.value
                    ? [{ level_2_display: value.display }]
                    : [],
                }));
              }}
              value={race?.level_2_code || race?.code}
            />
          </Box>
        </Box>
        <Box sx={detailFormInputSx as SystemStyleObject}>
          <FormControl sx={{ width: "100%" }}>
            <DatePicker
              label="Date of birth"
              date={date}
              onChange={(value) => {
                setDate(value);
                // Format date as yyyy-MM-dd for API
                const formattedValue = value
                  ? format(value, "yyyy-MM-dd")
                  : value;
                setTouchedFields((touchedFields) => ({
                  ...touchedFields,
                  birth_date: formattedValue,
                }));
              }}
            />
          </FormControl>
          {userType === "patient" ? (
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Pronouns"
                value={details.pronouns}
                onChange={(value) => {
                  setDetails((details) => ({ ...details, pronouns: value }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    pronouns: value,
                  }));
                }}
              />
            </FormControl>
          ) : (
            <Box sx={{ width: "100%" }} />
          )}
        </Box>
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Patient Number (From Patient Tracker)"
                value={details.patient_number}
                onChange={(value) => {
                  setDetails((details) => ({
                    ...details,
                    patient_number: value,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    patient_number: value,
                  }));
                }}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Epic Mrn"
                value={details.epic_mrn}
                onChange={(value) => {
                  setDetails((details) => ({ ...details, epic_mrn: value }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    epic_mrn: value,
                  }));
                }}
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <SearchableSelect
                label="Patient Status"
                setSelectedItem={(value) => {
                  const patientStatus = value?.value || null;
                  setDetails((details) => ({
                    ...details,
                    patient_status: patientStatus,
                  }));
                  setTouchedFields((touchedFields) => {
                    return { ...touchedFields, patient_status: patientStatus };
                  });
                }}
                value={details.patient_status || null}
                options={patientStatusOptions}
              />
            </FormControl>
            {/* <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Engagement Status"
                value={details.engagement_status}
                onChange={(value) => {
                  setDetails((details) => ({ ...details, engagement_status: value }));
                  setTouchedFields((touchedFields) => ({ ...touchedFields, engagement_status: value }));
                }}
              />
            </FormControl> */}
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <DatePicker
                label="Referral Date"
                date={referralDate}
                onChange={(value) => {
                  setReferralDate(value);
                  const formattedValue = value
                    ? format(value, "yyyy-MM-dd")
                    : value;
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    referral_date: formattedValue,
                  }));
                }}
              />
            </FormControl>
            <Box sx={{ width: "100%" }} />
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <SearchableSelect
                label="Communications"
                setSelectedItem={(value) => {
                  const communications = value?.value || null;
                  setDetails((details) => ({ ...details, communications }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    communications,
                  }));
                }}
                value={getCommunicationsValue(details)}
                options={communicationOptions}
                disabled={!canManagePreferences}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <SearchableSelect
                label="Program"
                setSelectedItem={(value) => {
                  const program = value?.value || null;
                  setDetails((details) => ({ ...details, program: program }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    program: program,
                  }));
                }}
                value={details.program || null}
                options={programOptions}
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <DatePicker
                label="Enrolled Date"
                date={enrolledDate}
                onChange={(value) => {
                  setEnrolledDate(value);
                  const formattedValue = value
                    ? format(value, "yyyy-MM-dd")
                    : value;
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    enrolled_date: formattedValue,
                  }));
                }}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Health System"
                value={details.health_system}
                onChange={(value) => {
                  setDetails((details) => ({
                    ...details,
                    health_system: value,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    health_system: value,
                  }));
                }}
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <AsyncSearchSelectV2
                label="Hospital"
                value={
                  details.hospital
                    ? { value: details.hospital, label: details.hospital }
                    : null
                }
                onChange={(value) => {
                  const hospital = getSingleOptionValue(value);
                  setDetails((details) => ({ ...details, hospital: hospital }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    hospital: hospital,
                  }));
                }}
                fetchOptions={fetchHospitals}
                createOption={createHospital}
                createNewOptionText="Add new hospital"
                minSearchLength={3}
                noOptionsText="No hospitals found"
                variant="outlined"
                fullWidth
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Main Point Of Contact At Practice"
                value={mainContactAtPractice}
                onChange={(value) => {
                  setDetails((details) => ({
                    ...details,
                    health_system_contact: value,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    health_system_contact: value,
                  }));
                }}
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <SearchableSelect
                label="Visit Type"
                setSelectedItem={(value) => {
                  const visitType = value?.value || null;
                  setDetails((details) => ({
                    ...details,
                    visit_type: visitType,
                    patient_type: visitType,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    visit_type: visitType,
                    patient_type: visitType,
                  }));
                }}
                value={details.visit_type || details.patient_type || null}
                options={visitTypeOptions}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <AsyncSearchSelectV2
                label="Service Line"
                value={
                  details.service_line
                    ? {
                        value: details.service_line,
                        label: details.service_line,
                      }
                    : null
                }
                onChange={(value) => {
                  const serviceLine = getSingleOptionValue(value);
                  setDetails((details) => ({
                    ...details,
                    service_line: serviceLine,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    service_line: serviceLine,
                  }));
                }}
                fetchOptions={fetchServiceLines}
                minSearchLength={3}
                noOptionsText="No service lines found"
                variant="outlined"
                fullWidth
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <DatePicker
                label="Surgery Date"
                date={pendingSurgeryDay || surgeryDay}
                error={!!surgeryDateError}
                helperText={surgeryDateError}
                onChange={(value) => {
                  if (!value) {
                    setSurgeryDateError("Surgery Date is required");
                    return;
                  }

                  if (!isValid(value) || value.getFullYear() <= 1900) {
                    setSurgeryDateError("Enter a valid Surgery Date");
                    return;
                  }

                  setSurgeryDateError("");
                  const formattedValue = format(value, "yyyy-MM-dd");
                  const acceptedValue =
                    surgeryDay && isValid(surgeryDay)
                      ? format(surgeryDay, "yyyy-MM-dd")
                      : null;

                  if (formattedValue === acceptedValue) return;

                  const today = new Date();
                  const localToday = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate(),
                  );
                  const localSurgeryDay = new Date(
                    value.getFullYear(),
                    value.getMonth(),
                    value.getDate(),
                  );

                  if (
                    localSurgeryDay > localToday &&
                    details.surgery_status !== "Scheduled"
                  ) {
                    setPendingSurgeryDay(value);
                    return;
                  }

                  applySurgeryDate(value);
                }}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <SearchableSelect
                label="Surgery Status"
                setSelectedItem={(value) => {
                  const surgeryStatus = value?.value || null;
                  setDetails((details) => ({
                    ...details,
                    surgery_status: surgeryStatus,
                  }));
                  setTouchedFields((touchedFields) => {
                    return { ...touchedFields, surgery_status: surgeryStatus };
                  });
                }}
                value={details.surgery_status || null}
                options={trackerSurgeryStatusOptions}
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <AsyncSearchSelectV2
                label="Surgery Type"
                value={
                  details.surgery_type
                    ? {
                        value: details.surgery_type,
                        label: details.surgery_type,
                      }
                    : null
                }
                onChange={(value) => {
                  const surgeryType = getSingleOptionValue(value);
                  setDetails((details) => ({
                    ...details,
                    surgery_type: surgeryType,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    surgery_type: surgeryType,
                  }));
                }}
                fetchOptions={fetchSurgeryTypes}
                minSearchLength={3}
                noOptionsText="No surgery types found"
                variant="outlined"
                fullWidth
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <AsyncSearchSelectV2
                label="Surgeon"
                value={
                  details.surgeon
                    ? { value: details.surgeon, label: details.surgeon }
                    : null
                }
                onChange={(value) => {
                  const surgeon = getSingleOptionValue(value);
                  setDetails((details) => ({ ...details, surgeon: surgeon }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    surgeon: surgeon,
                  }));
                }}
                fetchOptions={fetchSurgeons}
                createOption={createSurgeon}
                createNewOptionText="Add new surgeon"
                minSearchLength={3}
                noOptionsText="No surgeons found"
                variant="outlined"
                fullWidth
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box
            sx={{
              margin: (theme) => theme.spacing(1.5),
              display: "flex",
              flexWrap: "wrap",
              gap: (theme) => theme.spacing(2),
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={details.outcomes_collected || false}
                  onChange={(e) => {
                    setDetails((details) => ({
                      ...details,
                      outcomes_collected: e.target.checked,
                    }));
                    setTouchedFields((touchedFields) => ({
                      ...touchedFields,
                      outcomes_collected: e.target.checked,
                    }));
                  }}
                />
              }
              label="Outcomes Collected"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={details.deceased || false}
                  onChange={(e) => {
                    setDetails((details) => ({
                      ...details,
                      deceased: e.target.checked,
                    }));
                    setTouchedFields((touchedFields) => ({
                      ...touchedFields,
                      deceased: e.target.checked,
                    }));
                  }}
                />
              }
              label="Deceased"
            />
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <TextField
                label="Length of Stay"
                type="number"
                value={details.length_of_stay ?? ""}
                inputProps={{ min: 0, step: "any" }}
                onChange={(event) => {
                  const lengthOfStay =
                    event.target.value === ""
                      ? null
                      : Number(event.target.value);
                  setDetails((details) => ({
                    ...details,
                    length_of_stay: lengthOfStay,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    length_of_stay: lengthOfStay,
                  }));
                }}
                fullWidth
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <SearchableSelect
                label="Postoperative ED Visit"
                setSelectedItem={(value) => {
                  const postoperativeEdVisit = value?.value || null;
                  setDetails((details) => ({
                    ...details,
                    postoperative_ed_visit: postoperativeEdVisit,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    postoperative_ed_visit: postoperativeEdVisit,
                  }));
                }}
                value={details.postoperative_ed_visit || null}
                options={postoperativeOutcomeOptions}
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <DatePicker
                label="Postoperative ED Visit Date"
                date={postoperativeEdVisitDate}
                onChange={(value) => {
                  setPostoperativeEdVisitDate(value);
                  const formattedValue = value
                    ? format(value, "yyyy-MM-dd")
                    : null;
                  setDetails((details) => ({
                    ...details,
                    postoperative_ed_visit_date: formattedValue,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    postoperative_ed_visit_date: formattedValue,
                  }));
                }}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <SearchableSelect
                label="Postoperative Readmission"
                setSelectedItem={(value) => {
                  const postoperativeReadmission = value?.value || null;
                  setDetails((details) => ({
                    ...details,
                    postoperative_readmission: postoperativeReadmission,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    postoperative_readmission: postoperativeReadmission,
                  }));
                }}
                value={details.postoperative_readmission || null}
                options={postoperativeOutcomeOptions}
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <DatePicker
                label="Postoperative Readmission Date"
                date={postoperativeReadmissionDate}
                onChange={(value) => {
                  setPostoperativeReadmissionDate(value);
                  const formattedValue = value
                    ? format(value, "yyyy-MM-dd")
                    : null;
                  setDetails((details) => ({
                    ...details,
                    postoperative_readmission_date: formattedValue,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    postoperative_readmission_date: formattedValue,
                  }));
                }}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Cancellation Reason"
                value={details.cancellation_reason}
                onChange={(value) => {
                  setDetails((details) => ({
                    ...details,
                    cancellation_reason: value,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    cancellation_reason: value,
                  }));
                }}
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="PCP"
                value={details.pcp_physician}
                onChange={(value) => {
                  setDetails((details) => ({
                    ...details,
                    pcp_physician: value,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    pcp_physician: value,
                  }));
                }}
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="FIN"
                value={details.fin}
                onChange={(value) => {
                  setDetails((details) => ({ ...details, fin: value }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    fin: value,
                  }));
                }}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Outreach Assignee"
                value={details.outreach_assignee}
                onChange={(value) => {
                  setDetails((details) => ({
                    ...details,
                    outreach_assignee: value,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    outreach_assignee: value,
                  }));
                }}
              />
            </FormControl>
          </Box>
        )}
        {userType === "practitioner" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Short Bio"
                value={details.biography}
                onChange={(value) => {
                  setDetails((details) => ({ ...details, biography: value }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    biography: value,
                  }));
                }}
                placeholder="ex. 18 years of experience managing chronic pain"
              />
            </FormControl>
          </Box>
        )}
        <Box sx={detailFormInputSx as SystemStyleObject}>
          <FormControl sx={{ width: "100%" }}>
            {tagsV1 ? (
              <TagsInput
                tags={details.tag_list}
                onChange={(value) => {
                  setDetails((details) => ({ ...details, tag_list: value }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    tag_list: value,
                  }));
                }}
              />
            ) : (
              <TagsInputDeprecated
                tags={details.tag_list.length ? details.tag_list : []}
                onChange={(value) => {
                  setDetails((details) => ({ ...details, tag_list: value }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    tag_list: value,
                  }));
                }}
              />
            )}
          </FormControl>
        </Box>
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <AsyncSearchSelectV2
                label="Comorbidities"
                value={mapStringsToOptions(details.comorbidities)}
                onChange={(value) => {
                  const comorbidities = mapSelectionToStrings(value);
                  setDetails((details) => ({
                    ...details,
                    comorbidities: comorbidities,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    comorbidities: comorbidities,
                  }));
                }}
                fetchOptions={fetchComorbidities}
                createOption={createComorbidity}
                createNewOptionText="Add new comorbidity"
                noOptionsText="No comorbidities found"
                variant="outlined"
                fullWidth
                multiple
                showPopupIcon
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <AsyncSearchSelectV2
                label="Clearances"
                value={mapStringsToOptions(details.clearances)}
                onChange={(value) => {
                  const clearances = mapSelectionToStrings(value);
                  setDetails((details) => ({
                    ...details,
                    clearances: clearances,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    clearances: clearances,
                  }));
                }}
                fetchOptions={fetchClearances}
                createOption={createClearance}
                createNewOptionText="Add new clearance"
                noOptionsText="No clearances found"
                variant="outlined"
                fullWidth
                multiple
                showPopupIcon
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box sx={detailFormInputSx as SystemStyleObject}>
            <FormControl sx={{ width: "100%" }}>
              <TextFieldInput
                label="Notes"
                value={details.patient_notes || ""}
                onChange={(value) => {
                  setDetails((details) => ({
                    ...details,
                    patient_notes: value,
                  }));
                  setTouchedFields((touchedFields) => ({
                    ...touchedFields,
                    patient_notes: value,
                  }));
                }}
                multiline
                minRows={3}
              />
            </FormControl>
          </Box>
        )}
        {userType === "patient" && (
          <Box
            sx={{
              margin: (theme) => theme.spacing(1.5),
              display: "flex",
              flexWrap: "wrap",
              gap: (theme) => theme.spacing(2),
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={details.nih_grant_patient || false}
                  onChange={(e) => {
                    setDetails((details) => ({
                      ...details,
                      nih_grant_patient: e.target.checked,
                    }));
                    setTouchedFields((touchedFields) => ({
                      ...touchedFields,
                      nih_grant_patient: e.target.checked,
                    }));
                  }}
                />
              }
              label="NIH Grant Patient"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={details.opt_in_sms ?? true}
                  disabled={!canManagePreferences}
                  onChange={(e) => {
                    setDetails((details) => ({
                      ...details,
                      opt_in_sms: e.target.checked,
                    }));
                    setTouchedFields((touchedFields) => ({
                      ...touchedFields,
                      opt_in_sms: e.target.checked,
                    }));
                  }}
                />
              }
              label="SMS opt-in"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={details.opt_in_email ?? true}
                  disabled={!canManagePreferences}
                  onChange={(e) => {
                    setDetails((details) => ({
                      ...details,
                      opt_in_email: e.target.checked,
                    }));
                    setTouchedFields((touchedFields) => ({
                      ...touchedFields,
                      opt_in_email: e.target.checked,
                    }));
                  }}
                />
              }
              label="Email opt-in"
            />
          </Box>
        )}
      </Paper>
      <Modal
        open={!!pendingSurgeryDay}
        onClose={closeSurgeryDateConfirmation}
        title="Confirm surgery date change"
        primaryButtonText="Confirm"
        onPrimaryButtonClick={confirmSurgeryDateChange}
      >
        {pendingSurgeryDay &&
          `Changing the Surgery Date to ${format(
            pendingSurgeryDay,
            "yyyy-MM-dd",
          )} will also change Surgery Status to Scheduled.`}
      </Modal>
    </Section>
  );
};

export default DetailsFormSection;
